import React, { useState } from 'react';
import { ArrowRight, Briefcase, Users, Heart, Coffee, Brain, Globe, Clock, CheckCircle2, MapPin, Award, Star } from 'lucide-react';
import JobApplicationModal from '../components/JobApplicationModal';

const Careers = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');

  const handleApply = (position: string) => {
    setSelectedPosition(position);
    setIsModalOpen(true);
  };

  const positions = [
    {
      title: "Senior Full Stack Developer",
      type: "Full-time",
      location: "Remote / Chandler, AZ",
      description: "Join our team as a Senior Full Stack Developer and help build innovative solutions using modern technologies.",
      requirements: [
        "5+ years of experience with React, Node.js, and TypeScript",
        "Experience with cloud platforms (AWS/Azure)",
        "Strong understanding of software architecture",
        "Excellent problem-solving skills"
      ]
    },
    {
      title: "DevOps Engineer",
      type: "Full-time",
      location: "Remote / Chandler, AZ",
      description: "Help us build and maintain our infrastructure and deployment pipelines.",
      requirements: [
        "3+ years of DevOps experience",
        "Strong knowledge of Docker and Kubernetes",
        "Experience with CI/CD pipelines",
        "Infrastructure as Code expertise"
      ]
    },
    {
      title: "UI/UX Designer",
      type: "Full-time",
      location: "Remote / Chandler, AZ",
      description: "Create beautiful and intuitive user interfaces for our enterprise applications.",
      requirements: [
        "3+ years of UI/UX design experience",
        "Proficiency in Figma and design tools",
        "Understanding of user-centered design principles",
        "Experience with design systems"
      ]
    }
  ];

  const benefits = [
    {
      icon: <Globe className="w-8 h-8 text-yellow-400" />,
      title: "Remote-First Culture",
      description: "Work from anywhere in the world with our distributed team."
    },
    {
      icon: <Brain className="w-8 h-8 text-yellow-400" />,
      title: "Continuous Learning",
      description: "Annual learning budget and regular skill development workshops."
    },
    {
      icon: <Heart className="w-8 h-8 text-yellow-400" />,
      title: "Health & Wellness",
      description: "Comprehensive health insurance and wellness programs."
    },
    {
      icon: <Coffee className="w-8 h-8 text-yellow-400" />,
      title: "Work-Life Balance",
      description: "Flexible hours and unlimited PTO policy."
    },
    {
      icon: <Star className="w-8 h-8 text-yellow-400" />,
      title: "Career Growth",
      description: "Clear career progression and mentorship opportunities."
    },
    {
      icon: <Award className="w-8 h-8 text-yellow-400" />,
      title: "Competitive Pay",
      description: "Above-market compensation and equity packages."
    }
  ];

  const values = [
    {
      title: "Innovation First",
      description: "We embrace new technologies and ideas to solve complex problems."
    },
    {
      title: "Customer Success",
      description: "Our clients' success is our success. We go above and beyond."
    },
    {
      title: "Continuous Learning",
      description: "We believe in constant growth and skill development."
    },
    {
      title: "Work-Life Harmony",
      description: "We value balance and support flexible working arrangements."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Join Our Team</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Build the future of enterprise software with us. We're looking for passionate
              individuals who want to make a difference.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Users className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Our Values</h2>
            <p className="text-lg text-gray-600">
              These core values guide everything we do and shape our company culture.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Briefcase className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Why Work With Us?</h2>
            <p className="text-lg text-gray-600">
              We offer competitive benefits and a great work environment.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="mb-6">{benefit.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Open Positions</h2>
            <p className="text-lg text-gray-600">
              Join our team and help build the next generation of enterprise software.
            </p>
          </div>
          <div className="max-w-4xl mx-auto space-y-8">
            {positions.map((position, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{position.title}</h3>
                    <div className="flex items-center space-x-4 text-gray-600">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {position.type}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {position.location}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleApply(position.title)}
                    className="mt-4 md:mt-0 inline-flex items-center px-6 py-3 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
                  >
                    Apply Now
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
                <p className="text-gray-600 mb-6">{position.description}</p>
                <div className="space-y-3">
                  <h4 className="font-semibold">Requirements:</h4>
                  {position.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                      <span className="text-gray-600">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Don't See the Right Fit?</h2>
            <p className="text-xl text-gray-300 mb-8">
              We're always looking for talented individuals. Send us your resume and let's talk!
            </p>
            <button
              onClick={() => handleApply('General Application')}
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Submit General Application
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Job Application Modal */}
      <JobApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        position={selectedPosition}
      />
    </div>
  );
};

export default Careers;